// Plaid Link integration — loads the official widget from cdn.plaid.com
// and exposes a thin wrapper around `window.Plaid.create({...}).open()`.
//
// Why this isn't part of plaid_client.ts: Link is a JS widget delivered
// from Plaid's CDN, not a REST endpoint. Loading it pulls in a separate
// script-src host that callers should be aware of. Keeping the Link
// concerns isolated lets us:
//   - lazy-load the CDN script only when the user clicks "Connect"
//   - mock Link in tests by stubbing window.Plaid
//   - reuse the same module for "Add another bank" + "Re-link this bank"
//     flows in C5 (Manage Banks UI)
//
// The orchestration (link_token create → Link → onSuccess → exchange →
// vault save) lives in main.ts where the rest of the connect UX is.
//
// Plaid Link docs: https://plaid.com/docs/link/web/

// ── Plaid Link's runtime API (minimal subset we actually call) ────

/** What Plaid passes to onSuccess. Lots of fields omitted — we use
 *  metadata.institution mostly, plus account info for debugging. */
export interface PlaidLinkSuccessMetadata {
  institution: { name: string; institution_id: string } | null;
  accounts: Array<{ id: string; name: string; mask: string | null; type: string; subtype: string | null }>;
  link_session_id: string;
  transfer_status?: string;
  /** Public token is also passed positionally — this field mirrors it
   *  for callers that prefer the metadata shape. */
  public_token?: string;
}

export interface PlaidLinkExitMetadata {
  status: string | null;
  institution: { name: string; institution_id: string } | null;
  link_session_id: string;
  request_id: string;
}

export interface PlaidLinkError {
  error_type: string;
  error_code: string;
  error_message: string;
  display_message: string | null;
}

export interface PlaidLinkHandler {
  open(): void;
  exit(options?: { force?: boolean }): void;
  destroy(): void;
}

interface PlaidLinkConfig {
  token: string;
  onSuccess: (publicToken: string, metadata: PlaidLinkSuccessMetadata) => void;
  onExit?: (err: PlaidLinkError | null, metadata: PlaidLinkExitMetadata) => void;
  onEvent?: (eventName: string, metadata: Record<string, unknown>) => void;
  onLoad?: () => void;
  /** OAuth banks require receivedRedirectUri to resume a Link session
   *  after the user returns from the OAuth flow. Not needed for non-
   *  OAuth banks. */
  receivedRedirectUri?: string;
}

interface PlaidLinkGlobal {
  create(config: PlaidLinkConfig): PlaidLinkHandler;
}

declare global {
  interface Window {
    Plaid?: PlaidLinkGlobal;
  }
}

// ── script loader (idempotent) ────────────────────────────────────

const PLAID_LINK_SRC = "https://cdn.plaid.com/link/v2/stable/link-initialize.js";
let loadPromise: Promise<void> | null = null;

/** Lazy-loads the Plaid Link script from cdn.plaid.com. Resolves once
 *  window.Plaid is available, rejects on script load failure. Subsequent
 *  calls return the same Promise — safe to call from every "Connect"
 *  click without leaking <script> tags. */
export function loadPlaidLink(): Promise<void> {
  if (loadPromise) return loadPromise;
  if (typeof window !== "undefined" && window.Plaid) {
    // Already loaded (e.g. by a previous page in a SPA navigation).
    loadPromise = Promise.resolve();
    return loadPromise;
  }
  loadPromise = new Promise<void>((resolve, reject) => {
    if (typeof document === "undefined") {
      reject(new Error("Plaid Link can only be loaded in a browser."));
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${PLAID_LINK_SRC}"]`);
    if (existing) {
      // Another loader inserted the tag but hasn't fired load yet — wait.
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Plaid Link script failed to load (cached tag).")));
      return;
    }
    const tag = document.createElement("script");
    tag.src = PLAID_LINK_SRC;
    tag.async = true;
    // No SRI hash — Plaid bumps the link-initialize.js bundle frequently
    // and doesn't publish hashes. CSP script-src allowlists cdn.plaid.com
    // so a foreign script can't squat the source.
    tag.addEventListener("load", () => {
      if (window.Plaid) resolve();
      else reject(new Error("Plaid Link loaded but window.Plaid is undefined."));
    });
    tag.addEventListener("error", () => {
      reject(new Error("Plaid Link script failed to load. Check connectivity to cdn.plaid.com."));
    });
    document.head.appendChild(tag);
  });
  // If load fails, allow a retry on the next call.
  loadPromise.catch(() => {
    loadPromise = null;
  });
  return loadPromise;
}

// ── open Link ──────────────────────────────────────────────────────

export interface OpenLinkOptions {
  /** link_token from /link/token/create. */
  linkToken: string;
  /** Called when the user successfully links a bank. Caller exchanges
   *  the public_token + persists the resulting access_token. */
  onSuccess: (publicToken: string, metadata: PlaidLinkSuccessMetadata) => void;
  /** Called when the user closes Link without linking (and on errors).
   *  err is non-null when Plaid surfaced an error to the user. */
  onExit?: (err: PlaidLinkError | null, metadata: PlaidLinkExitMetadata) => void;
  /** Fires for every Plaid Link lifecycle event — OPEN, TRANSITION_VIEW,
   *  SELECT_INSTITUTION, SUBMIT_CREDENTIALS, HANDOFF, ERROR, EXIT, etc.
   *  Critical for diagnosing apparent hangs: if onSuccess/onExit never
   *  fire, the onEvent stream still tells you where in the lifecycle
   *  Link actually got. Always wire this through to a log channel. */
  onEvent?: (eventName: string, metadata: Record<string, unknown>) => void;
  /** Fires once Plaid Link's CDN bundle has finished bootstrapping and
   *  the Link iframe is ready to be opened. Lets the caller distinguish
   *  "CDN script never loaded" from "loaded but iframe init hung".
   *  Always optional — handler.open() queues until ready regardless. */
  onLoad?: () => void;
  /** Set during the OAuth-return path: this is the full URL the bank
   *  redirected to (window.location.href, including the
   *  ?oauth_state_id=... query param Plaid added). Plaid Link reads
   *  the state id from it and resumes the original Link session.
   *  Don't set on the initial Link open; only on the resume after a
   *  full-page OAuth redirect. */
  receivedRedirectUri?: string;
}

/** Open a Plaid Link session. Loads the CDN script if needed, then
 *  creates the widget with the supplied link_token. Resolves when EITHER
 *  onExit fires (the normal close path) OR onSuccess fires followed by
 *  a 3-second grace period for onExit (a defence against the
 *  "post-HANDOFF onExit never fires" case observed in production —
 *  most commonly when an extension DOM-removes the Plaid iframe after
 *  HANDOFF, killing the postMessage channel before onExit can be
 *  dispatched). The grace period gives Plaid the typical onSuccess →
 *  onExit interval (low milliseconds) plus headroom.
 *
 *  Also resolves the Promise as a hard timeout after 5 minutes
 *  regardless, so a fully-stalled Plaid Link doesn't lock the caller
 *  in an unrecoverable state. The caller's status will surface as
 *  "Plaid Link did not complete" so the user can retry. */
export async function openPlaidLink(opts: OpenLinkOptions): Promise<void> {
  await loadPlaidLink();
  if (!window.Plaid) {
    throw new Error("Plaid Link is not available even after loading the CDN script.");
  }
  return new Promise<void>((resolveOuter, rejectOuter) => {
    let resolved = false;
    let onExitTimer: ReturnType<typeof setTimeout> | null = null;
    let hardTimer: ReturnType<typeof setTimeout> | null = null;

    const finalize = (rejectErr?: Error): void => {
      if (resolved) return;
      resolved = true;
      if (onExitTimer) clearTimeout(onExitTimer);
      if (hardTimer) clearTimeout(hardTimer);
      try {
        handler.destroy();
      } catch {
        // already destroyed, no-op
      }
      if (rejectErr) rejectOuter(rejectErr);
      else resolveOuter();
    };

    const handler = window.Plaid!.create({
      token: opts.linkToken,
      receivedRedirectUri: opts.receivedRedirectUri,
      onSuccess: (publicToken, metadata) => {
        try {
          opts.onSuccess(publicToken, metadata);
        } finally {
          // Standard path: Plaid fires onExit right after onSuccess. Wait
          // a short grace period for it. If onExit never fires (Layer
          // flow / iframe yanked / extension interference) we resolve
          // anyway — we already have the public_token so the connect
          // flow can proceed.
          if (onExitTimer) clearTimeout(onExitTimer);
          onExitTimer = setTimeout(() => finalize(), 3000);
        }
      },
      onExit: (err, metadata) => {
        try {
          if (opts.onExit) opts.onExit(err, metadata);
        } finally {
          finalize();
        }
      },
      onEvent: opts.onEvent,
      onLoad: opts.onLoad,
    });

    // Hard ceiling. If neither onSuccess nor onExit fires within 5
    // minutes, abandon the session and surface a clear error.
    hardTimer = setTimeout(
      () => finalize(new Error("Plaid Link did not complete within 5 minutes. Try again.")),
      5 * 60 * 1000,
    );

    handler.open();
  });
}

// ── testing seam ──────────────────────────────────────────────────

export const _testing = {
  /** Reset the lazy script loader. Used by tests so a fresh module
   *  state is observable per test. */
  resetLoader(): void {
    loadPromise = null;
  },
  scriptSrc(): string {
    return PLAID_LINK_SRC;
  },
};
